import Vue, { CreateElement } from '@src/vue'
import { Component, Prop } from 'vue-property-decorator'
import async from 'async'

import { Subtitle1, Subtitle2 } from '@src/elements/typography/typography'
import { Input } from '@src/elements/input/input'
import { Button } from '@src/elements/button/button'
import { FormInputRow } from '@src/routes/members/components/body-measurements/form-input-row'
import { MetricData } from '@src/data/body-measurement'
import { Spacer } from '@src/elements/spacer/spacer'
import { BodyMeasurementsCard } from '@src/routes/members/components/body-measurements/body-measurements-card'
import { Divider } from '@src/elements/divider/divider'
import EventBus from '@src/lib/event_bus'
import { API } from '@src/lib/api_path'
import ApiClient from '@src/lib/api_client'
import Member from '@src/data/member'

import './bmi.sass'
import {
  convertLengthForDisplaying, convertToCM,
  convertToKg,
  convertWeightForDisplaying,
  LengthUnit,
  WeightUnit
} from '@src/util/weight'

@Component({
  computed: {
    member () {
      return this.$store.state.selectedMember
    },
    weightUnit () {
      return this.$store.state.club.weightUnit
    },
    heightUnit () {
      return this.$store.state.club.weightUnit === WeightUnit.KG ? LengthUnit.CM : LengthUnit.IN
    }
  }
})
export class BMI extends Vue {

  @Prop({ required: false, default: 'm' })
  gender: string

  @Prop({ required: true })
  height: MetricData[]

  @Prop({ required: true })
  weight: MetricData[]

  protected weightUnit: WeightUnit
  protected heightUnit: LengthUnit
  protected currentWeight: number | null = null

  // user inserts value in local measuring unit. Before saving or computing BMI, the value must be converted to kg.
  protected currentHeight: number | null = null

  private member: Member
  private apiClient: ApiClient = new ApiClient()

  constructor () {
    super()
  }

  buildBmiClass (bmi, min, max, gender): string {
    if (bmi && gender === this.gender) {
      if (min <= bmi && bmi < max) {
        return 'column selected'
      }
    }
    return 'column'
  }

  setWeight (value) {
    if (!isNaN(Number(value))) {
      this.currentWeight = value
    }
  }

  setHeight (value) {
    if (!isNaN(Number(value))) {
      this.currentHeight = value
    }
  }

  computeBMI (body: { weight, height }) {
    if (!body.weight || !body.height) {
      return null
    }

    return +(convertToKg(body.weight, this.weightUnit) / Math.pow(convertToCM(body.height, this.heightUnit) / 100, 2)).toFixed(1)
  }

  render (h: CreateElement) {
    const bmi = this.computeBMI({ weight: this.currentWeight, height: this.currentHeight })

    return <div class='measurements-section bmi'>
      <Subtitle1 class='title'>{this.$t('BODY_MEASUREMENTS.BODY_MASS_INDEX')} {'(BMI)'}</Subtitle1>
      {this.renderCard(h, bmi)}
    </div>
  }

  renderCard (h: CreateElement, bmi: number | null) {
    return <BodyMeasurementsCard class='bmi-wrapper' type='bmi'>
      {this.renderForm(h, bmi)}
      {this.renderTable(h, bmi)}
    </BodyMeasurementsCard>
  }

  renderForm (h: CreateElement, bmi: number | null) {
    return <div slot='left'>
      <div class={'bmi-remove-btn-wrapper'}>
        <Subtitle1 class='title'>{this.$t('BODY_MEASUREMENTS.BMI.CALCULATED_BMI')}</Subtitle1>
        <Button type='remove' label={`${bmi || '-'}`} />
        <Spacer />
      </div>

      <div class='row bmi-inputs-row'>
        <FormInputRow>
          <Input ref='weightInput' value={`${this.currentWeight ? this.currentWeight : ''}`} valueChanged={this.setWeight} type='decimal' label={this.$t(`BODY_MEASUREMENTS.BMI.BODY_WEIGHT.${this.weightUnit.toUpperCase()}`)}
                 class='bmiInputs' />
        </FormInputRow>
        <FormInputRow>
          <Input ref='heightInput' value={`${this.currentHeight ? this.currentHeight : ''}`} valueChanged={this.setHeight} type='decimal' label={this.$t(`BODY_MEASUREMENTS.BMI.BODY_HEIGHT_UNITS.${this.heightUnit.toUpperCase()}`)}
                 class='bmiInputs' />
        </FormInputRow>
      </div>
      <div class={'bmi-device-rows'}>
        <div class='row bmi-device-row'>
          <Subtitle2 class='title'>{this.$t(`BODY_MEASUREMENTS.BMI.BODY_WEIGHT.${this.weightUnit.toUpperCase()}`)}</Subtitle2>
          <Subtitle2 class='title'>{`${this.currentWeight ? this.currentWeight : ''}`}</Subtitle2>
        </div>
        <div class='row bmi-device-row'>
          <Subtitle2 class='title'>{this.$t(`BODY_MEASUREMENTS.BMI.BODY_HEIGHT_UNITS.${this.heightUnit.toUpperCase()}`)}</Subtitle2>
          <Subtitle2 class='title'>{`${this.currentHeight ? this.currentHeight : ''}`}</Subtitle2>
        </div>
      </div>
    </div>
  }

  renderTable (h: CreateElement, bmi: number | null) {
    return <div slot='right'>
      <div class='row'>
        <div class='column' />
        <Subtitle1 class='column header center'>{this.$t('BODY_MEASUREMENTS.BMI.BMI_MALE')}</Subtitle1>
        <Subtitle1 class='column header center'>{this.$t('BODY_MEASUREMENTS.BMI.BMI_FEMALE')}</Subtitle1>
      </div>
      <Divider />
      <div class='row'>
        <Subtitle1 class='column header'>{this.$t('BODY_MEASUREMENTS.BMI.UNDERWEIGHT')}</Subtitle1>
        <Subtitle1 class={`bmi-column-from-small-screens ${this.buildBmiClass(bmi, 0, 20, 'm')}`}>{this.$t('PAGE.MEMBERS.STATUS.LESS_THAN', { value: 20 })}</Subtitle1>
        <Subtitle1 class={`bmi-column-from-small-screens ${this.buildBmiClass(bmi, 0, 19, 'f')}`}>{this.$t('PAGE.MEMBERS.STATUS.LESS_THAN', { value: 19 })}</Subtitle1>
        <Subtitle1 class={`bmi-column-on-devices ${this.buildBmiClass(bmi, 0, 20, 'm')}`}>{'< 20'}</Subtitle1>
        <Subtitle1 class={`bmi-column-on-devices ${this.buildBmiClass(bmi, 0, 19, 'f')}`}>{'< 19'}</Subtitle1>
      </div>
      <Divider />
      <div class='row'>
        <Subtitle1 class='column header'>{this.$t('BODY_MEASUREMENTS.BMI.NORMAL_WEIGHT')}</Subtitle1>
        <Subtitle1 class={this.buildBmiClass(bmi, 20, 26, 'm')}>20-25</Subtitle1>
        <Subtitle1 class={this.buildBmiClass(bmi, 19, 25, 'f')}>19-24</Subtitle1>
      </div>
      <Divider />
      <div class='row'>
        <Subtitle1 class='column header'>{this.$t('BODY_MEASUREMENTS.BMI.OVERWEIGHT')}</Subtitle1>
        <Subtitle1 class={this.buildBmiClass(bmi, 26, 31, 'm')}>26-30</Subtitle1>
        <Subtitle1 class={this.buildBmiClass(bmi, 25, 31, 'f')}>25-30</Subtitle1>
      </div>
      <Divider />
      <div class='row'>
        <Subtitle1 class='column header'>{this.$t('BODY_MEASUREMENTS.BMI.OBESITY')}</Subtitle1>
        <Subtitle1 class={this.buildBmiClass(bmi, 31, 40, 'm')}>31-40</Subtitle1>
        <Subtitle1 class={this.buildBmiClass(bmi, 31, 40, 'f')}>31-40</Subtitle1>
      </div>
      <Divider />
      <div class='row'>
        <Subtitle1 class='column header'>{this.$t('BODY_MEASUREMENTS.BMI.HIGH_OBESITY')}</Subtitle1>
        <Subtitle1 class={`bmi-column-from-small-screens ${this.buildBmiClass(bmi, 40, Number.MAX_VALUE, 'm')}`}>{this.$t('PAGE.MEMBERS.STATUS.MORE_THAN', { value: 40 })}</Subtitle1>
        <Subtitle1 class={`bmi-column-from-small-screens ${this.buildBmiClass(bmi, 40, Number.MAX_VALUE, 'f')}`}>{this.$t('PAGE.MEMBERS.STATUS.MORE_THAN', { value: 40 })}</Subtitle1>
        <Subtitle1 class={`bmi-column-on-devices ${this.buildBmiClass(bmi, 40, Number.MAX_VALUE, 'm')}`}>{'> 40'}</Subtitle1>
        <Subtitle1 class={`bmi-column-on-devices ${this.buildBmiClass(bmi, 40, Number.MAX_VALUE, 'f')}`}>{'> 40'}</Subtitle1>
      </div>
    </div>
  }

  created () {
    EventBus.on('applyMeasurementsChange', this.save)

    this.initCurrentMeasurements()
  }

  beforeDestroy () {
    EventBus.off('applyMeasurementsChange', this.save)
  }

  initCurrentMeasurements () {
    const weights = this.weight.slice().sort((a, b) => a.effectiveAt > b.effectiveAt ? -1 : a.effectiveAt < b.effectiveAt ? 1 : 0)
    const bodyWeight = weights.length ? weights[0].value : null
    this.currentWeight = this.weight.length > 0 ? convertWeightForDisplaying(bodyWeight, this.weightUnit, 2) : null
    const highestHeight = this.height.length > 0 ? this.height.slice().sort((a, b) => a.effectiveAt > b.effectiveAt ? -1 : a.effectiveAt < b.effectiveAt ? 1 : 0)[0].value : null
    this.currentHeight = highestHeight ? convertLengthForDisplaying(highestHeight, this.heightUnit, 2) : null
  }

  save (formType) {
    if (formType === 'bmi') {
      if (this.currentWeight && this.currentHeight) {

        async.waterfall(
          [
            { body_measurement: { metric: 'body_weight', value: convertToKg(this.currentWeight, this.weightUnit), effective_at: new Date().toISOString() } },
            { body_measurement: { metric: 'height', value: convertToCM(this.currentHeight, this.heightUnit), effective_at: new Date().toISOString() } }
          ].map((measurement) => {
            return (done) => this.submitMeasurement(measurement, done)
          }),
          () => {
            EventBus.trigger('reloadBodyMeasurements')
            this.$store.dispatch('showToast', { message: this.$t('BODY_MEASUREMENTS.SAVE_SUCCESS'), fadeOut: true })
          })
      }
    }
  }

  submitMeasurement (measurement, done) {
    return this.apiClient
      .post(API.TRAINERS_API.BODY_MEASUREMENTS.replace('{memberId}', `${this.member.id}`), measurement)
      .catch((error) => console.log('cannot save body measurement', error))
      .finally(() => done(null))
  }
}
